import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
	checklistItems: [],
	loading: false,
	error: null,
};

const FETCH_CHECKLIST_ITEMS_REQUEST = "FETCH_CHECKLIST_ITEMS_REQUEST";
const FETCH_CHECKLIST_ITEMS_SUCCESS = "FETCH_CHECKLIST_ITEMS_SUCCESS";
const FETCH_CHECKLIST_ITEMS_FAILURE = "FETCH_CHECKLIST_ITEMS_FAILURE";
const CREATE_CHECKLIST_ITEM_REQUEST = "CREATE_CHECKLIST_ITEM_REQUEST";
const CREATE_CHECKLIST_ITEM_SUCCESS = "CREATE_CHECKLIST_ITEM_SUCCESS";
const CREATE_CHECKLIST_ITEM_FAILURE = "CREATE_CHECKLIST_ITEM_FAILURE";
const UPDATE_CHECKLIST_ITEM_REQUEST = "UPDATE_CHECKLIST_ITEM_REQUEST";
const UPDATE_CHECKLIST_ITEM_SUCCESS = "UPDATE_CHECKLIST_ITEM_SUCCESS";
const UPDATE_CHECKLIST_ITEM_FAILURE = "UPDATE_CHECKLIST_ITEM_FAILURE";
const DELETE_CHECKLIST_ITEM_REQUEST = "DELETE_CHECKLIST_ITEM_REQUEST";
const DELETE_CHECKLIST_ITEM_SUCCESS = "DELETE_CHECKLIST_ITEM_SUCCESS";
const DELETE_CHECKLIST_ITEM_FAILURE = "DELETE_CHECKLIST_ITEM_FAILURE";
const REORDER_CHECKLIST_ITEMS_REQUEST = "REORDER_CHECKLIST_ITEMS_REQUEST";
const REORDER_CHECKLIST_ITEMS_SUCCESS = "REORDER_CHECKLIST_ITEMS_SUCCESS";
const REORDER_CHECKLIST_ITEMS_FAILURE = "REORDER_CHECKLIST_ITEMS_FAILURE";

// Helper to save checklist item inspection types (same pattern as saveVehicleServiceRequirements)
const saveChecklistItemInspectionTypes = async (
	checklistItemId,
	inspectionTypeIds,
	userAgency,
	userId,
) => {
	try {
		const service = getService(
			serviceNames.perChecklistItemInspectionTypes,
		);

		// Get existing inspection types for this checklist item
		const existingTypes = await service.find({
			query: {
				checklistItemId: checklistItemId,
				isDeleted: false,
				$limit: -1,
			},
		});

		const existingData = existingTypes?.data || existingTypes || [];
		const existingTypeIds = existingData.map((t) => t.inspectionTypeId);
		const newTypeIds = inspectionTypeIds || [];

		// Find types to delete (exist in DB but not in new list)
		const toDelete = existingData.filter(
			(t) => !newTypeIds.includes(t.inspectionTypeId),
		);

		// Find types to create (in new list but not in DB)
		const toCreate = newTypeIds.filter(
			(id) => !existingTypeIds.includes(id),
		);

		// Delete removed ones
		for (const type of toDelete) {
			await service.patch(type.id, {
				isDeleted: true,
				deletedBy: userId,
				deletedDateTime: new Date().toISOString(),
			});
		}

		// Create new ones
		for (const inspectionTypeId of toCreate) {
			await service.create({
				checklistItemId: checklistItemId,
				inspectionTypeId: inspectionTypeId,
				agencyId: userAgency,
				isDeleted: false,
				createdBy: userId,
			});
		}

		return true;
	} catch (error) {
		console.error("Error saving checklist item inspection types:", error);
		throw error;
	}
};

export const getChecklistItems = (userAgency) => async (dispatch) => {
	const service = getService(serviceNames.perChecklistItems);
	const query = {
		isDeleted: false,
		agencyId: userAgency,
		$sort: { sortOrder: 1 },
	};

	return await asyncThunkWrapper({
		dispatch,
		types: {
			request: FETCH_CHECKLIST_ITEMS_REQUEST,
			success: FETCH_CHECKLIST_ITEMS_SUCCESS,
			failure: FETCH_CHECKLIST_ITEMS_FAILURE,
		},
		apiCall: async () => {
			const response = await service.find({ query });
			const items = response?.data || response || [];

			// Fetch inspection types for each item
			const typesService = getService(
				serviceNames.perChecklistItemInspectionTypes,
			);

			const itemsWithTypes = await Promise.all(
				items.map(async (item) => {
					try {
						const typesResponse = await typesService.find({
							query: {
								checklistItemId: item.id,
								isDeleted: false,
								$limit: -1,
							},
						});

						const typesData =
							typesResponse?.data || typesResponse || [];

						return {
							...item,
							inspectionTypes: typesData.map(
								(t) => t.inspectionTypeId,
							),
						};
					} catch (error) {
						console.error(
							`Error fetching inspection types for item ${item.id}:`,
							error,
						);
						return {
							...item,
							inspectionTypes: [],
						};
					}
				}),
			);

			return itemsWithTypes;
		},
		errorMessagePrefix: "Failed to fetch checklist items",
	});
};

export const createChecklistItem =
	(itemData, inspectionTypeIds = [], userAgency, userId) =>
	async (dispatch) => {
		const service = getService(serviceNames.perChecklistItems);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_CHECKLIST_ITEM_REQUEST,
				success: CREATE_CHECKLIST_ITEM_SUCCESS,
				failure: CREATE_CHECKLIST_ITEM_FAILURE,
			},
			apiCall: async () => {
				// Get current max sortOrder
				const existingItems = await service.find({
					query: {
						isDeleted: false,
						agencyId: userAgency,
						$sort: { sortOrder: -1 },
						$limit: 1,
					},
				});

				const maxSortOrder =
					existingItems?.data?.[0]?.sortOrder ||
					existingItems?.[0]?.sortOrder ||
					0;

				const newItem = await service.create({
					...itemData,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
					isActive: true,
					sortOrder: maxSortOrder + 1,
				});

				// Save inspection types
				if (inspectionTypeIds.length > 0 && newItem.id) {
					await saveChecklistItemInspectionTypes(
						newItem.id,
						inspectionTypeIds,
						userAgency,
						userId,
					);
				}

				return {
					...newItem,
					inspectionTypes: inspectionTypeIds,
				};
			},
			successMessage: "Checklist item created successfully",
			errorMessagePrefix: "Failed to create checklist item",
		});
	};

export const updateChecklistItem =
	(id, itemData, inspectionTypeIds = [], userAgency, userId) =>
	async (dispatch) => {
		const service = getService(serviceNames.perChecklistItems);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_CHECKLIST_ITEM_REQUEST,
				success: UPDATE_CHECKLIST_ITEM_SUCCESS,
				failure: UPDATE_CHECKLIST_ITEM_FAILURE,
			},
			apiCall: async () => {
				const item = await service.patch(id, {
					...itemData,
					updatedBy: userId,
				});

				// Update inspection types
				await saveChecklistItemInspectionTypes(
					id,
					inspectionTypeIds,
					userAgency,
					userId,
				);

				return {
					...item,
					inspectionTypes: inspectionTypeIds,
				};
			},
			successMessage: "Checklist item updated successfully",
			errorMessagePrefix: "Failed to update checklist item",
		});
	};

export const toggleChecklistItemActive =
	(id, isActive, userId) => async (dispatch) => {
		const service = getService(serviceNames.perChecklistItems);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_CHECKLIST_ITEM_REQUEST,
				success: UPDATE_CHECKLIST_ITEM_SUCCESS,
				failure: UPDATE_CHECKLIST_ITEM_FAILURE,
			},
			apiCall: async () => {
				const item = await service.patch(id, {
					isActive: isActive,
					updatedBy: userId,
				});
				return item;
			},
			successMessage: isActive
				? "Checklist item enabled"
				: "Checklist item disabled",
			errorMessagePrefix: "Failed to update checklist item",
		});
	};

export const reorderChecklistItems = (items, userId) => async (dispatch) => {
	const service = getService(serviceNames.perChecklistItems);
	return await asyncThunkWrapper({
		dispatch,
		types: {
			request: REORDER_CHECKLIST_ITEMS_REQUEST,
			success: REORDER_CHECKLIST_ITEMS_SUCCESS,
			failure: REORDER_CHECKLIST_ITEMS_FAILURE,
		},
		apiCall: async () => {
			// Update sortOrder for all items
			const updatePromises = items.map((item, index) =>
				service.patch(item.id, {
					sortOrder: index + 1,
					updatedBy: userId,
				}),
			);

			await Promise.all(updatePromises);
			return items;
		},
		successMessage: "Checklist items reordered",
		errorMessagePrefix: "Failed to reorder checklist items",
	});
};

export const deleteChecklistItem = (id, userId) => async (dispatch) => {
	const service = getService(serviceNames.perChecklistItems);
	const patchData = {
		isDeleted: true,
		deletedBy: userId,
		deletedDateTime: new Date().toISOString(),
	};

	return await asyncThunkWrapper({
		dispatch,
		types: {
			request: DELETE_CHECKLIST_ITEM_REQUEST,
			success: DELETE_CHECKLIST_ITEM_SUCCESS,
			failure: DELETE_CHECKLIST_ITEM_FAILURE,
		},
		apiCall: async () => {
			// Soft delete the checklist item
			await service.patch(id, patchData);

			// Also soft delete associated inspection types
			const typesService = getService(
				serviceNames.perChecklistItemInspectionTypes,
			);
			const types = await typesService.find({
				query: {
					checklistItemId: id,
					isDeleted: false,
				},
			});

			const existingTypes = types?.data || types || [];
			for (const type of existingTypes) {
				await typesService.patch(type.id, {
					isDeleted: true,
					deletedBy: userId,
					deletedDateTime: new Date().toISOString(),
				});
			}

			return id;
		},
		transformResult: () => id,
		successMessage: "Checklist item deleted successfully",
		errorMessagePrefix: "Failed to delete checklist item",
	});
};

export default function checklistItemsReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_CHECKLIST_ITEMS_REQUEST:
		case CREATE_CHECKLIST_ITEM_REQUEST:
		case UPDATE_CHECKLIST_ITEM_REQUEST:
		case DELETE_CHECKLIST_ITEM_REQUEST:
		case REORDER_CHECKLIST_ITEMS_REQUEST:
			return { ...state, loading: true, error: null };

		case FETCH_CHECKLIST_ITEMS_SUCCESS:
			return {
				...state,
				loading: false,
				checklistItems: action.payload,
			};

		case CREATE_CHECKLIST_ITEM_SUCCESS:
			return {
				...state,
				loading: false,
				checklistItems: [...state.checklistItems, action.payload],
			};

		// FIXED: Handle both single item update and full array reorder
		case UPDATE_CHECKLIST_ITEM_SUCCESS:
			return {
				...state,
				loading: false,
				checklistItems: Array.isArray(action.payload)
					? action.payload
					: state.checklistItems.map((item) =>
							item.id === action.payload.id
								? { ...item, ...action.payload }
								: item,
						),
			};

		case REORDER_CHECKLIST_ITEMS_SUCCESS:
			return {
				...state,
				loading: false,
				checklistItems: Array.isArray(action.payload)
					? action.payload
					: state.checklistItems,
			};

		case DELETE_CHECKLIST_ITEM_SUCCESS:
			return {
				...state,
				loading: false,
				checklistItems: state.checklistItems.filter(
					(i) => i.id !== action.payload,
				),
			};

		case FETCH_CHECKLIST_ITEMS_FAILURE:
		case CREATE_CHECKLIST_ITEM_FAILURE:
		case UPDATE_CHECKLIST_ITEM_FAILURE:
		case DELETE_CHECKLIST_ITEM_FAILURE:
		case REORDER_CHECKLIST_ITEMS_FAILURE:
			return { ...state, loading: false, error: action.payload };

		default:
			return state;
	}
}
